import { SessionMetadataSchema, type SessionMetadata, type SessionId } from '@/schemas';
import { db } from './client';

export type SessionListScope = 'active' | 'archived' | 'all';

const matchesScope = (session: SessionMetadata, scope: SessionListScope): boolean => {
  if (session.deletedAt) {
    return false;
  }

  if (scope === 'active') {
    return !session.archivedAt;
  }
  if (scope === 'archived') {
    return Boolean(session.archivedAt);
  }
  return true;
};

const sortSessions = (sessions: SessionMetadata[]): SessionMetadata[] => {
  return sessions.toSorted((a, b) => b.updatedAt - a.updatedAt);
};

/**
 * Get all active (non-deleted) sessions, sorted by most recent first
 */
export const getSessions = async (
  scope: SessionListScope = 'active',
): Promise<SessionMetadata[]> => {
  const sessions = await db.sessionMetadata
    .filter((session) => matchesScope(session, scope))
    .toArray();
  const parsed = sessions.map((session) => SessionMetadataSchema.parse(session));
  return sortSessions(parsed);
};

export const getActiveSessions = async (): Promise<SessionMetadata[]> => getSessions('active');

export const getArchivedSessions = async (): Promise<SessionMetadata[]> => getSessions('archived');

/**
 * Get session metadata by ID
 */
export const getSessionMetadata = async (
  sessionId: SessionId,
): Promise<SessionMetadata | undefined> => {
  const record = await db.sessionMetadata.get(sessionId);
  return record ? SessionMetadataSchema.parse(record) : undefined;
};

/**
 * Search sessions by name
 */
export const searchSessions = async (query: string): Promise<SessionMetadata[]> => {
  const lowerQuery = query.toLowerCase();

  const sessions = await db.sessionMetadata
    .filter(
      (session) =>
        matchesScope(session, 'active') &&
        Boolean(session.name && session.name.toLowerCase().includes(lowerQuery)),
    )
    .toArray();
  return sortSessions(sessions.map((session) => SessionMetadataSchema.parse(session)));
};

/**
 * Get sessions by status
 */
export const getSessionsByStatus = async (
  status: 'ongoing' | 'paused' | 'finished',
): Promise<SessionMetadata[]> => {
  const sessions = await db.sessionMetadata
    .where('status')
    .equals(status)
    .and((session) => matchesScope(session, 'active'))
    .toArray();
  return sortSessions(sessions.map((session) => SessionMetadataSchema.parse(session)));
};

// Removed sessions by game definition; no longer applicable with inline configs
